/**
 * The old `subtabs.*` and `rules.*` switches GUS still reads for the newer City
 * Satisfaction switches, and how it reads them.
 *
 * GUS (branch feat/satisfaction-depth, satisfaction/flags.py: ALIASES,
 * RULE_ALIASES, DETECTOR_ALIASES; design spec 2026-10-08 section 10):
 *   - an old switch is read only when the new switch is absent;
 *   - a new switch with several old ones is off only when all of them are off;
 *   - an absent old switch counts as on.
 * No switch has both a `subtabs.*` and a `rules.*` alias, so one list per switch
 * says it all. `subtabs.map` is no alias: GUS ignores it, the map is always on.
 *
 * The portal never writes an old switch on the admin's behalf and never writes
 * the new one to match an old one: it shows where each switch now lives and
 * says when the two disagree. Paths are relative to the tab node
 * `modules.atlas.tabs.satisfaction`.
 */
import { SATISFACTION_PATH } from './satisfactionSettings';

/** New switch -> the old switches GUS reads for it. */
export const SATISFACTION_ALIASES: Readonly<Record<string, readonly string[]>> = {
  chart: ['subtabs.trends'],
  compare: ['subtabs.compare'],
  stands_out: ['subtabs.discover'],
  'detectors.changed': ['rules.worse', 'rules.improved'],
  'detectors.gap': ['rules.spread'],
  'detectors.unheard': ['rules.unheard'],
  'detectors.spike': ['rules.recent_shift'],
  learn_from: ['rules.mentor'],
  who: ['rules.dialect'],
  why: ['rules.praise', 'rules.reasons', 'rules.mood_gap', 'rules.survey', 'rules.felt'],
  facilities: ['rules.facility_low', 'rules.facility_high'],
};

/** Old switch -> its new home. */
export const NEW_HOME: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(SATISFACTION_ALIASES).flatMap(([home, olds]) => olds.map((old) => [old, home])),
);

const ABSENT = Symbol('absent');

function readAt(root: unknown, path: string): unknown {
  let node: unknown = root;
  for (const part of path.split('.')) {
    if (node === null || typeof node !== 'object' || Array.isArray(node) || !(part in (node as object))) return ABSENT;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

/** GUS resolve_enabled: absent, null, and an object without `enabled` are on; `enabled: false` is off. */
export function resolveEnabled(v: unknown): boolean {
  if (v === ABSENT || v === null || v === undefined) return true;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'object' && !Array.isArray(v) && 'enabled' in (v as object)) {
    return (v as Record<string, unknown>).enabled !== false;
  }
  return true;
}

/**
 * Whether the new switch is "present", so its old switches are not read.
 * A detector needs a real boolean (GUS detector_on falls back to the old
 * switches on anything else); a page counts as present once its node is
 * stored at all, as GUS's other section aliases do (flags.py section_on).
 */
function newKeyDecides(home: string, v: unknown): boolean {
  if (v === ABSENT || v === null || v === undefined) return false;
  if (!home.startsWith('detectors.')) return true;
  return typeof v === 'boolean'
    || (typeof v === 'object' && !Array.isArray(v) && typeof (v as Record<string, unknown>).enabled === 'boolean');
}

export interface OldSwitchReading {
  /** relative to the tab node */
  path: string;
  set: boolean;
  on: boolean;
}

export interface AliasReading {
  /** the new switch, relative to the tab node */
  home: string;
  /** the new switch is stored, so GUS ignores the old ones */
  homeDecides: boolean;
  homeOn: boolean;
  old: OldSwitchReading[];
  /** what GUS shows */
  on: boolean;
  /** the new switch is set and a stored old switch says the opposite */
  disagree: boolean;
}

/** How GUS reads `home` for this tenant's stored flags (the whole feature_flags object). */
export function readAlias(storedFlags: unknown, home: string): AliasReading {
  const tab = readAt(storedFlags, SATISFACTION_PATH);
  const homeValue = readAt(tab, home);
  const homeDecides = newKeyDecides(home, homeValue);
  const homeOn = resolveEnabled(homeValue);
  const old = (SATISFACTION_ALIASES[home] || []).map((path) => {
    const v = readAt(tab, path);
    return { path, set: v !== ABSENT && v !== null && v !== undefined, on: resolveEnabled(v) };
  });
  // Off only when every old switch is off; an absent one counts as on.
  const on = homeDecides ? homeOn : old.length === 0 || old.some((o) => o.on);
  const disagree = homeDecides && old.some((o) => o.set && o.on !== homeOn);
  return { home, homeDecides, homeOn, old, on, disagree };
}

/** The alias relation a manifest node takes part in, if any. */
export function aliasRoleOf(nodePath: string): { role: 'home' | 'old'; home: string } | null {
  const prefix = `${SATISFACTION_PATH}.`;
  if (!nodePath.startsWith(prefix)) return null;
  const rel = nodePath.slice(prefix.length);
  if (SATISFACTION_ALIASES[rel]) return { role: 'home', home: rel };
  if (NEW_HOME[rel]) return { role: 'old', home: NEW_HOME[rel] };
  return null;
}
